import { StoredValue } from 'record-it-utils/dataStorage/StoredValue/StoredValue';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { SerializedImmutableStopwatch, ImmutableStopwatch, ImmutableStopwatchInfo } from '../ImmutableStopwatch/ImmutableStopwatch';
export class StopwatchManager {
  private readonly storedLastId = new StoredValue<number>({
    key: 'StopwatchManager.lastStopwatchId'
  }, {});
  private readonly storedStopwatches = new StoredValue<Array<ImmutableStopwatch>, Array<SerializedImmutableStopwatch>>({
    key: 'StopwatchManager.stopwatches'
  }, {
    serializeValue: sws => sws.map(sw => sw.toJSON()),
    deserializeValue: sws => sws.map(sw => ImmutableStopwatch.fromJson(sw))
  });
  public async init(): Promise<void> {
    await this.storedStopwatches.init(() => ({
      version: 1,
      value: []
    }));
    await this.storedLastId.init(() => ({
      version: 1,
      value: 0
    }));
  }
  public async flush(): Promise<void> {
    await this.storedStopwatches.flush();
    await this.storedLastId.flush();
  }
  public clear(): void {
    this.storedStopwatches.setValue([]);
    this.storedLastId.setValue(0);
  }
  public bindStopwatchInfos(callback: (infos: Array<ImmutableStopwatchInfo>) => void): Disposable {
    return this.storedStopwatches.bindValue(sws => {
      callback(sws.map(sw => sw.getInfo()));
    });
  }
  public onStopwatchInfosChanged(callback: (infos: Array<ImmutableStopwatchInfo>) => void): Disposable {
    return this.storedStopwatches.onValueChanged(sws => {
      callback(sws.map(sw => sw.getInfo()));
    });
  }
  public startNew(userGroupId: string): {
    id: number;
  } {
    const sw = ImmutableStopwatch.create(this.getNextId(), userGroupId);
    this.storedStopwatches.setValue([...this.storedStopwatches.getValue(), sw.start()]);
    return {
      id: sw.id
    };
  }
  public start(id: number): void {
    this.modifyStopwatchById(id, sw => sw.start());
  }
  public pause(id: number): void {
    this.modifyStopwatchById(id, sw => sw.pause());
  }
  public remove(id: number): void {
    this.storedStopwatches.setValue(this.storedStopwatches.getValue().filter(sw => sw.id !== id));
  }
  public getById(id: number): ImmutableStopwatchInfo {
    const info = this.storedStopwatches.getValue().find(sw => sw.id === id)?.getInfo() ?? null;
    assertNotNullOrUndefined(info, `no stopwatch found for "${id}"`);
    return info;
  }
  public findById(id: number): ImmutableStopwatchInfo | null {
    return this.storedStopwatches.getValue().find(sw => sw.id === id)?.getInfo() ?? null;
  }
  public getStopwatchInfos(): Array<ImmutableStopwatchInfo> {
    return this.storedStopwatches.getValue().map(sw => sw.getInfo());
  }
  private modifyStopwatchById(id: number, modifyCallback: (sw: ImmutableStopwatch) => ImmutableStopwatch): void {
    let found = false;
    const stopWatches = this.storedStopwatches.getValue().map(sw => {
      if (sw.id !== id) {
        return sw;
      }
      found = true;
      return modifyCallback(sw);
    });

    // typescript doesn't know that .map get's called synchronously, so it thinks that `found` will always be false here
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (found) {
      this.storedStopwatches.setValue(stopWatches);
    }
  }
  private getNextId(): number {
    const id = this.storedLastId.getValue() + 1;
    this.storedLastId.setValue(id);
    return id;
  }
}